import {
  type InfiniteData,
  QueryClient,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  useMutation,
  type UseMutationOptions,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { type Batcher, create as createBatcher, windowedFiniteBatchScheduler } from '@yornaath/batshit';
import { useCallback } from 'react';

import type { ListActionsRequest, ListActionsResponse } from '@src/gen/ai/h2o/authorization/v1/action_api_pb';
import type { ListPoliciesRequest, ListPoliciesResponse } from '@src/gen/ai/h2o/authorization/v1/policy_api_pb';
import type {
  ResolvePermissionRequest,
  ResolvePermissionResponse,
} from '@src/gen/ai/h2o/authorization/v1/query_api_pb';
import type {
  AddActionStatementsRequest,
  AddActionStatementsResponse,
  CreateRoleRequest,
  CreateRoleResponse,
  DeleteRoleRequest,
  DeleteRoleResponse,
  ListRolesRequest,
  ListRolesResponse,
  RemoveActionStatementsRequest,
  RemoveActionStatementsResponse,
  UpdateRoleRequest,
  UpdateRoleResponse,
} from '@src/gen/ai/h2o/authorization/v1/role_api_pb';
import type {
  ListRoleBindingsRequest,
  ListRoleBindingsResponse,
} from '@src/gen/ai/h2o/authorization/v1/role_binding_api_pb';
import type { RoleBinding } from '@src/gen/ai/h2o/authorization/v1/role_binding_pb';
import type { Role } from '@src/gen/ai/h2o/authorization/v1/role_pb';
import type {
  ListGroupsRequest,
  ListGroupsResponse,
  ListUserGroupsRequest,
  ListUserGroupsResponse,
} from '@src/gen/ai/h2o/user/v1/group_api_pb';
import type { Group } from '@src/gen/ai/h2o/user/v1/group_pb';
import type { ListUsersRequest, ListUsersResponse } from '@src/gen/ai/h2o/user/v1/user_api_pb';
import { type User, userName } from '@src/gen/ai/h2o/user/v1/user_pb';
import type {
  CreateLabelRequest,
  CreateLabelResponse,
  DeleteLabelRequest,
  DeleteLabelResponse,
  ListLabelsRequest,
  ListLabelsResponse,
  UpdateLabelRequest,
  UpdateLabelResponse,
} from '@src/gen/ai/h2o/workspace/v1/label_api_pb';
import type { Label } from '@src/gen/ai/h2o/workspace/v1/label_pb';
import type {
  CreateTagRequest,
  CreateTagResponse,
  DeleteTagRequest,
  DeleteTagResponse,
  ListTagsRequest,
  ListTagsResponse,
  UpdateTagRequest,
  UpdateTagResponse,
} from '@src/gen/ai/h2o/workspace/v1/tag_api_pb';
import type { Tag } from '@src/gen/ai/h2o/workspace/v1/tag_pb';
import type {
  AddLabelsRequest,
  AddLabelsResponse,
  AddTagsRequest,
  AddTagsResponse,
  ArchiveWorkspaceResponse,
  CreateWorkspaceRequest,
  CreateWorkspaceResponse,
  DeleteWorkspaceResponse,
  ListAllWorkspacesRequest,
  ListAllWorkspacesResponse,
  ListWorkspacesRequest,
  ListWorkspacesResponse,
  RemoveLabelsRequest,
  RemoveLabelsResponse,
  RemoveTagsRequest,
  RemoveTagsResponse,
  UndeleteWorkspaceResponse,
  UpdateWorkspaceRequest,
  UpdateWorkspaceResponse,
} from '@src/gen/ai/h2o/workspace/v1/workspace_api_pb';
import { Workspace, workspaceName } from '@src/gen/ai/h2o/workspace/v1/workspace_pb';
import { useCloudPlatformDiscovery, useUser } from '@shared/lib/hooks';
import { GrpcStatusCode, isGrpcGatewayErrorResponse } from '@shared/services/appstore/HTTPResponse';

import {
  addLabelsToWorkspace,
  addRoleActionStatements,
  addTagsToWorkspace,
  archiveWorkspace,
  batchResolvePermissions,
  createLabel,
  createRole,
  createTag,
  createWorkspace,
  deleteLabel,
  deleteRole,
  deleteTag,
  deleteWorkspace,
  FixedResolvePermissionRequest,
  getGroup,
  getRole,
  getUser,
  getWorkspace,
  listActions,
  listAllWorkspaces,
  listGroups,
  listLabels,
  listPolicies,
  listRoleBindings,
  listRoles,
  listTags,
  listUserGroups,
  listUsers,
  listWorkspaces,
  removeLabelsFromWorkspace,
  removeRoleActionStatements,
  removeTagsFromWorkspace,
  unarchiveWorkspace,
  undeleteWorkspace,
  updateLabel,
  updateRole,
  updateTag,
  updateWorkspace,
} from './authz.functions';

/**
 * For testing: force these actions to be denied for all permission checks (nav, routes, entity fields, etc.).
 * All permission checks use the same batch endpoint; mocking here applies app-wide.
 * Set back to [] before committing.
 */
export const MOCK_DENIED_ACTIONS: string[] = [];

/**
 * For testing: force these actions to be allowed for all permission checks (nav, routes, entity fields, etc.).
 * Takes precedence over the API response but not over {@link MOCK_DENIED_ACTIONS}.
 * Set back to [] before committing.
 */
export const MOCK_ALLOWED_ACTIONS: string[] = [];

// Constants
export const defaultAppstoreWorkspaceName = 'workspaces/appstore';
export const defaultGlobalWorkspaceName = 'workspaces/global';

export const cacheKeys = {
  groupsAll: (params?: Pick<ListGroupsRequest, `filter`>) => {
    const baseKey: QueryKey = [`authz`, `groups`, `all`];
    if (!params) return baseKey;
    // set params to default values so cache key equals for not-set or set-to-empty
    const { filter = `` } = params;
    return baseKey.concat({ filter });
  },
  groupsOne: (groupId: string) => {
    return [`authz`, `groups`, `one`, groupId] as const;
  },
  userGroupsAll: (params: Pick<ListUserGroupsRequest, `parent`>) => {
    const baseKey: QueryKey = [`authz`, `userGroups`, `all`];
    if (!params) return baseKey;
    const { parent = `` } = params;
    return baseKey.concat({ parent });
  },
  tagsAll: () => {
    const baseKey: QueryKey = [`authz`, `tags`, `all`];
    return baseKey;
  },
  labelsAll: () => {
    const baseKey: QueryKey = [`authz`, `labels`, `all`];
    return baseKey;
  },
  permissionOne: (params: Pick<Required<ResolvePermissionRequest>, `action` | `resource` | `subject`>) =>
    [...cacheKeys.permissionAllForResource(params.resource), params.subject, params.action] as const,
  permissionAllForResource: (resource: string) => {
    return [`authz`, `permissions`, resource] as const;
  },
  roleBindingsAll: (params?: Pick<ListRoleBindingsRequest, `filter`>) => {
    const baseKey: QueryKey = [`authz`, `roleBindings`, `all`];
    if (!params) return baseKey;
    // set params to default values so cache key equals for not-set or set-to-empty
    const { filter = `` } = params;
    return baseKey.concat({ filter });
  },
  policiesAll: (params?: Pick<ListPoliciesRequest, `pageSize`>) => {
    const baseKey: QueryKey = [`authz`, `policies`, `all`];
    if (!params) return baseKey;
    const { pageSize = 0 } = params;
    return baseKey.concat({ pageSize });
  },
  rolesAll: (params?: Pick<ListRolesRequest, `filter` | `orderBy`>) => {
    const baseKey: QueryKey = [`authz`, `roles`, `all`];
    if (!params) return baseKey;
    const { filter = ``, orderBy = `` } = params;
    return baseKey.concat({ filter, orderBy });
  },
  roleOne: (roleName: string) => [`authz`, `roles`, `one`, roleName] as const,
  actionsAll: (params?: Pick<ListActionsRequest, `filter`>) => {
    const baseKey: QueryKey = [`authz`, `actions`, `all`];
    if (!params) return baseKey;
    const { filter = `` } = params;
    return baseKey.concat({ filter });
  },
  userAll: (params?: Pick<ListUsersRequest, `filter` | `showInactive`>) => {
    const baseKey: QueryKey = [`authz`, `users`, `all`];
    if (!params) return baseKey;
    // set params to default values so cache key equals for not-set or set-to-empty
    const { filter = ``, showInactive = false } = params;
    return baseKey.concat({ filter, showInactive });
  },
  userOne: (userId: string) => [`authz`, `users`, `one`, userId] as const,
  usersSearch: (params?: Pick<ListUsersRequest, `filter`>) => {
    const baseKey: QueryKey = [`authz`, `users`, `search`];
    if (!params) return baseKey;
    const { filter = `` } = params;
    return baseKey.concat({ filter });
  },
  groupsSearch: (params?: Pick<ListGroupsRequest, `filter`>) => {
    const baseKey: QueryKey = [`authz`, `groups`, `search`];
    if (!params) return baseKey;
    const { filter = `` } = params;
    return baseKey.concat({ filter });
  },
  workspacesAll: (params?: Pick<ListWorkspacesRequest, `filter` | `orderBy` | `showArchived` | `showDeleted`>) => {
    const baseKey: QueryKey = [`authz`, `workspaces`, `all`];
    if (!params) return baseKey;
    // set params to default values so cache key equals for not-set or set-to-empty
    const { filter = ``, orderBy = ``, showArchived = false, showDeleted = false } = params;
    return baseKey.concat({ filter, orderBy, showArchived, showDeleted });
  },
  workspacesMy: (params?: Pick<ListWorkspacesRequest, `filter` | `orderBy` | `showArchived` | `showDeleted`>) => {
    const baseKey: QueryKey = [`authz`, `workspaces`, `my`];
    if (!params) return baseKey;
    // set params to default values so cache key equals for not-set or set-to-empty
    const { filter = ``, orderBy = ``, showArchived = false, showDeleted = false } = params;
    return baseKey.concat({ filter, orderBy, showArchived, showDeleted });
  },
  workspaceOne: (workspaceId: string) => {
    return [`authz`, `workspaces`, `one`, workspaceId] as const;
  },
};

/**
 * Shared `select` functions for the infinite queries in this module.
 *
 * TanStack Query v5 treats `select` as part of the query observer's options: when the function's
 * reference changes it re-runs the transformation and produces a new `data` object, which cascades
 * into re-renders of everything derived from it. An arrow function written inline in a component
 * body is a new reference on every render, so these live at module scope where their identity is
 * stable for the lifetime of the app.
 *
 * They all follow the same convention: the flattened list of items is returned in `pages`, so
 * consumers read `query.data?.pages` and get a flat array instead of a page-per-entry array.
 */

/** Flattens the pages of `useQueryMyWorkspaces` / `useQueryAllWorkspaces` into a single workspace list. */
export const selectFlatWorkspaces = (
  data: InfiniteData<ListWorkspacesResponse | ListAllWorkspacesResponse, string | undefined>
): InfiniteData<Workspace, string | undefined> => ({
  pages: data.pages.flatMap((page) => page.workspaces || []),
  pageParams: data.pageParams,
});

/**
 * `select` for the workspace lists that hides one workspace already rendered elsewhere (the personal
 * / default workspace, which the switchers pin to the top). Returned from a hook so the function's
 * identity only changes when the excluded name does — an inline arrow would be a new reference on
 * every render and make TanStack Query re-run the transform and re-render every consumer.
 */
export const useSelectFlatWorkspacesExcluding = (excludedWorkspaceName?: string) =>
  useCallback(
    (data: InfiniteData<ListWorkspacesResponse, string | undefined>): InfiniteData<Workspace, string | undefined> => ({
      pages: data.pages
        .flatMap((page) => page.workspaces || [])
        .filter((workspace) => !excludedWorkspaceName || workspace.name !== excludedWorkspaceName),
      pageParams: data.pageParams,
    }),
    [excludedWorkspaceName]
  );

/**
 * Flattens the workspace pages into `{ key, text }` picker options (workspace id + display name),
 * the shape the workspace dropdowns and filter pickers render.
 */
export const selectWorkspacePickerOptions = (
  data: InfiniteData<ListWorkspacesResponse | ListAllWorkspacesResponse, string | undefined>
): InfiniteData<{ key: string; text: string }, string | undefined> => ({
  pages: data.pages
    .flatMap((page) => page.workspaces || [])
    .map((workspace) => {
      const id = workspace.name ? workspaceName.parse(workspace.name).workspace : '';
      return { key: id, text: workspace.displayName || id };
    }),
  pageParams: data.pageParams,
});

/** Flattens the pages of `useQueryRoleBindings` into a single role-binding list. */
export const selectFlatRoleBindings = (
  data: InfiniteData<ListRoleBindingsResponse, string | undefined>
): InfiniteData<RoleBinding, string | undefined> => ({
  pages: data.pages.flatMap((page) => page.roleBindings || []),
  pageParams: data.pageParams,
});

/** Flattens the pages of `useQueryUsers` into a single user list. */
export const selectFlatUsers = (
  data: InfiniteData<ListUsersResponse, string | undefined>
): InfiniteData<User, string | undefined> => ({
  pages: data.pages.flatMap((page) => page.users || []),
  pageParams: data.pageParams,
});

/** Flattens the pages of `useQueryGroups` into a single group list. */
export const selectFlatGroups = (
  data: InfiniteData<ListGroupsResponse, string | undefined>
): InfiniteData<Group, string | undefined> => ({
  pages: data.pages.flatMap((page) => page.groups || []),
  pageParams: data.pageParams,
});

/** Flattens the pages of `useQueryTags` into a single tag list. */
export const selectFlatTags = (
  data: InfiniteData<ListTagsResponse, string | undefined>
): InfiniteData<Tag, string | undefined> => ({
  pages: data.pages.flatMap((page) => page.tags || []),
  pageParams: data.pageParams,
});

/** Flattens the pages of `useQueryLabels` into a single label list. */
export const selectFlatLabels = (
  data: InfiniteData<ListLabelsResponse, string | undefined>
): InfiniteData<Label, string | undefined> => ({
  pages: data.pages.flatMap((page) => page.labels || []),
  pageParams: data.pageParams,
});

// Groups

export const getQueryOptionsGroup = <TData = Group>(
  authzUrl: string,
  groupName: string,
  queryClient?: QueryClient
): UseQueryOptions<Group, unknown, TData> => {
  return {
    initialData:
      queryClient &&
      (() => {
        const cachedListResponses = queryClient.getQueriesData<{ pages: ListGroupsResponse[] }>({
          queryKey: cacheKeys.groupsAll(),
          exact: false,
        });
        for (const [, queryData] of cachedListResponses) {
          if (queryData?.pages) {
            for (const cachedResponse of queryData.pages) {
              const cachedGroup = cachedResponse?.groups?.find((group) => group.name === groupName);
              if (cachedGroup) return cachedGroup;
            }
          }
        }
        return undefined;
      }),
    queryKey: cacheKeys.groupsOne(groupName),
    queryFn: async ({ signal }) => {
      const response = await getGroup(authzUrl, groupName, { signal });
      return response.group!;
    },
  };
};

export const useQueryGroup = <TData = Group>(
  groupName?: string,
  options?: Omit<UseQueryOptions<Group, unknown, TData>, `queryKey` | `queryFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();

  return useQuery({
    ...getQueryOptionsGroup<TData>(authzUrl, groupName || '', queryClient),
    ...options,
    enabled: Boolean(groupName) && options?.enabled !== false,
  });
};

export type UseQueryGroupsOptions<TData> = UseInfiniteQueryOptions<
  ListGroupsResponse,
  unknown,
  InfiniteData<TData, string | undefined>,
  QueryKey,
  string | undefined
>;

export const useQueryGroups = <TData = ListGroupsResponse>(
  params: Omit<ListGroupsRequest, `pageToken`> = {},
  options?: Omit<UseQueryGroupsOptions<TData>, `getNextPageParam` | `queryKey` | `queryFn` | `initialPageParam`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.groupsAll(params),
    queryFn: ({ pageParam, signal }) => listGroups(authzUrl, { ...params, pageToken: pageParam }, { signal }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

export type UseQueryUserGroupsOptions<TData> = UseInfiniteQueryOptions<
  ListUserGroupsResponse,
  unknown,
  InfiniteData<TData, string | undefined>,
  QueryKey,
  string | undefined
>;

export const useQueryUserGroups = <TData = ListUserGroupsResponse>(
  params: Omit<ListUserGroupsRequest, `pageToken`> = {},
  options?: Omit<UseQueryUserGroupsOptions<TData>, `getNextPageParam` | `queryKey` | `queryFn` | `initialPageParam`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.userGroupsAll(params),
    queryFn: ({ pageParam, signal }) => listUserGroups(authzUrl, { ...params, pageToken: pageParam }, { signal }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

// Helpers

const invalidateWorkspacesCache = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: cacheKeys.workspacesAll(), exact: false });
  queryClient.invalidateQueries({ queryKey: cacheKeys.workspacesMy(), exact: false });
};

const useWorkspaceStatusMutation = <TResponse>(
  mutation: (authzUrl: string, name: string) => Promise<TResponse>,
  options?: Omit<UseMutationOptions<TResponse, unknown, string>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || ``;
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async (name: string) => {
      const response = await mutation(authzUrl, name);
      invalidateWorkspacesCache(queryClient);
      const { workspace: workspaceId } = workspaceName.parse(name);
      queryClient.invalidateQueries({ queryKey: cacheKeys.workspaceOne(workspaceId), exact: true });
      return response;
    },
  });
};

// Tags

export type UseQueryTagsOptions<TData> = UseInfiniteQueryOptions<
  ListTagsResponse,
  unknown,
  InfiniteData<TData, string | undefined>,
  QueryKey,
  string | undefined
>;

export const useQueryTags = <TData = ListTagsResponse>(
  params: Omit<ListTagsRequest, `pageToken`> = {},
  options?: Omit<UseQueryTagsOptions<TData>, `getNextPageParam` | `queryKey` | `queryFn` | `initialPageParam`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.tagsAll(),
    queryFn: ({ pageParam, signal }) => listTags(authzUrl, { ...params, pageToken: pageParam }, { signal }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

const invalidateTagsCache = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({
    queryKey: cacheKeys.tagsAll(),
    exact: false,
  });
};

export const useMutationCreateTag = (
  options?: Omit<UseMutationOptions<CreateTagResponse, unknown, CreateTagRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: CreateTagRequest) => {
      const response = await createTag(authzUrl, request);
      invalidateTagsCache(queryClient);
      return response;
    },
  });
};

export const useMutationUpdateTag = (
  options?: Omit<UseMutationOptions<UpdateTagResponse, unknown, UpdateTagRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: UpdateTagRequest) => {
      const response = await updateTag(authzUrl, request);
      invalidateTagsCache(queryClient);
      invalidateWorkspacesCache(queryClient);
      return response;
    },
  });
};

export const useMutationDeleteTag = (
  options?: Omit<UseMutationOptions<DeleteTagResponse, unknown, DeleteTagRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: DeleteTagRequest) => {
      const response = await deleteTag(authzUrl, request);
      invalidateTagsCache(queryClient);
      invalidateWorkspacesCache(queryClient);
      return response;
    },
  });
};

// Labels

export type UseQueryLabelsOptions<TData> = UseInfiniteQueryOptions<
  ListLabelsResponse,
  unknown,
  InfiniteData<TData, string | undefined>,
  QueryKey,
  string | undefined
>;

export const useQueryLabels = <TData = ListLabelsResponse>(
  params: Omit<ListLabelsRequest, `pageToken`> = {},
  options?: Omit<UseQueryLabelsOptions<TData>, `getNextPageParam` | `queryKey` | `queryFn` | `initialPageParam`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.labelsAll(),
    queryFn: ({ pageParam, signal }) => listLabels(authzUrl, { ...params, pageToken: pageParam }, { signal }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

const invalidateLabelsCache = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({
    queryKey: cacheKeys.labelsAll(),
    exact: false,
  });
};

export const useMutationCreateLabel = (
  options?: Omit<UseMutationOptions<CreateLabelResponse, unknown, CreateLabelRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: CreateLabelRequest) => {
      const response = await createLabel(authzUrl, request);
      invalidateLabelsCache(queryClient);
      return response;
    },
  });
};

export const useMutationUpdateLabel = (
  options?: Omit<UseMutationOptions<UpdateLabelResponse, unknown, UpdateLabelRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: UpdateLabelRequest) => {
      const response = await updateLabel(authzUrl, request);
      invalidateLabelsCache(queryClient);
      invalidateWorkspacesCache(queryClient);
      return response;
    },
  });
};

export const useMutationDeleteLabel = (
  options?: Omit<UseMutationOptions<DeleteLabelResponse, unknown, DeleteLabelRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: DeleteLabelRequest) => {
      const response = await deleteLabel(authzUrl, request);
      invalidateLabelsCache(queryClient);
      invalidateWorkspacesCache(queryClient);
      return response;
    },
  });
};

// Permissions

/**
 * A cache object for batchers, structured by Authz base path (should be constant, but there is no strong guarantee)
 * The top-most WeakMap is here just to isolate batchers in tests.
 */
type PermissionsBatcher = Batcher<
  Map<FixedResolvePermissionRequest, ResolvePermissionResponse>,
  FixedResolvePermissionRequest,
  ResolvePermissionResponse
>;
const permissionBatcherCache = new WeakMap<QueryClient, Map<string, PermissionsBatcher>>();

const getPermissionBatcher = (queryClient: QueryClient, basePath: string) => {
  if (!permissionBatcherCache.has(queryClient)) {
    permissionBatcherCache.set(queryClient, new Map());
  }
  const queryClientBatchers = permissionBatcherCache.get(queryClient)!;
  if (!queryClientBatchers.has(basePath)) {
    queryClientBatchers.set(
      basePath,
      createBatcher({
        fetcher: async (requests: FixedResolvePermissionRequest[]) => {
          const { responses } = await batchResolvePermissions(basePath, { requests });
          const mockedResponses = responses!.map((res, i) => {
            const action = requests[i].action;
            if (MOCK_DENIED_ACTIONS.includes(action)) {
              return { ...res, allowed: false };
            }
            if (MOCK_ALLOWED_ACTIONS.includes(action)) {
              return { ...res, allowed: true };
            }
            return res;
          });
          const requestResponseEntries = requests.map((req, i) => [req, mockedResponses[i]] as const);
          return new Map(requestResponseEntries);
        },
        resolver: (batchedResponse, request) => batchedResponse.get(request)!,
        scheduler: windowedFiniteBatchScheduler({
          windowMs: 10,
          maxBatchSize: 1_000,
        }),
      })
    );
  }
  return queryClientBatchers.get(basePath)!;
};

export const getQueryOptionsPermission = <TData = ResolvePermissionResponse>(
  queryClient: QueryClient,
  basePath: string,
  req: FixedResolvePermissionRequest
): UseQueryOptions<ResolvePermissionResponse, unknown, TData> => {
  return {
    staleTime: 120 * 1_000, // consider permissions valid for 120 seconds after the last fetch
    queryKey: cacheKeys.permissionOne(req),
    queryFn: () => {
      return getPermissionBatcher(queryClient, basePath).fetch(req);
    },
  };
};

export const useQueryPermission = <TData = ResolvePermissionResponse>(
  req: FixedResolvePermissionRequest,
  options?: Omit<UseQueryOptions<ResolvePermissionResponse, unknown, TData>, `queryKey` | `queryFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  const baseOptions = getQueryOptionsPermission<TData>(queryClient, authzUrl, req);
  return useQuery({ ...options, ...baseOptions });
};

export const useQueryPermissionCurrentUser = <TData = ResolvePermissionResponse>(
  req: Omit<FixedResolvePermissionRequest, `subject`>,
  options?: Omit<UseQueryOptions<ResolvePermissionResponse, unknown, TData>, `queryKey` | `queryFn`>
) => {
  const currentUser = useUser();
  const currentUserName = userName.compile({ user: currentUser.id });
  return useQueryPermission<TData>(
    { ...req, subject: currentUserName },
    { ...options, enabled: options?.enabled !== false && !currentUser.isPublic }
  );
};

/** Query multiple permissions for the current user in parallel */
export const useQueryMultiplePermissionsCurrentUser = <TData = ResolvePermissionResponse>(
  reqs: Omit<FixedResolvePermissionRequest, 'subject'>[],
  options?: Omit<UseQueryOptions<ResolvePermissionResponse, unknown, TData>, 'queryKey' | 'queryFn'>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  const currentUser = useUser();
  const currentUserName = userName.compile({ user: currentUser.id });

  const queries = reqs.map((req) => ({
    ...getQueryOptionsPermission<TData>(queryClient, authzUrl, { ...req, subject: currentUserName }),
    ...options,
    enabled: options?.enabled !== false && !currentUser.isPublic,
  }));

  return useQueries({ queries });
};

export type UseQueryRoleBindingsOptions<TData> = UseInfiniteQueryOptions<
  ListRoleBindingsResponse,
  unknown,
  InfiniteData<TData, string | undefined>,
  QueryKey,
  string | undefined
>;

export const useQueryRoleBindings = <TData = ListRoleBindingsResponse>(
  request: Omit<ListRoleBindingsRequest, `pageToken`> = {},
  options?: Omit<UseQueryRoleBindingsOptions<TData>, `getNextPageParam` | `queryKey` | `queryFn` | `initialPageParam`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.roleBindingsAll(request),
    queryFn: ({ pageParam, signal }) => listRoleBindings(authzUrl, { ...request, pageToken: pageParam }, { signal }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

export type UseQueryPoliciesOptions<TData> = UseInfiniteQueryOptions<
  ListPoliciesResponse,
  unknown,
  InfiniteData<TData, string | undefined>,
  QueryKey,
  string | undefined
>;

export const useQueryPolicies = <TData = ListPoliciesResponse>(
  request: Omit<ListPoliciesRequest, `pageToken`> = {},
  options?: Omit<UseQueryPoliciesOptions<TData>, `getNextPageParam` | `queryKey` | `queryFn` | `initialPageParam`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.policiesAll(request),
    queryFn: ({ pageParam, signal }) => listPolicies(authzUrl, { ...request, pageToken: pageParam }, { signal }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

export const invalidateRolesCache = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: [`authz`, `roles`], exact: false });
};

export type UseQueryRolesOptions<TData> = UseInfiniteQueryOptions<
  ListRolesResponse,
  unknown,
  InfiniteData<TData, string | undefined>,
  QueryKey,
  string | undefined
>;

export const useQueryRoles = <TData = ListRolesResponse>(
  request: Omit<ListRolesRequest, `pageToken`> = {},
  options?: Omit<UseQueryRolesOptions<TData>, `getNextPageParam` | `queryKey` | `queryFn` | `initialPageParam`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.rolesAll(request),
    queryFn: ({ pageParam, signal }) => listRoles(authzUrl, { ...request, pageToken: pageParam }, { signal }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

export const useQueryRole = <TData = Role>(
  roleName: string | undefined,
  options?: Omit<UseQueryOptions<Role, unknown, TData>, `queryKey` | `queryFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useQuery({
    ...options,
    enabled: options?.enabled !== false && Boolean(roleName),
    queryKey: cacheKeys.roleOne(roleName || ''),
    queryFn: async ({ signal }) => {
      const response = await getRole(authzUrl, { name: roleName }, { signal });
      if (!response.role) throw new Error('Role not found');
      return response.role;
    },
  });
};

export type UseQueryActionsOptions<TData> = UseInfiniteQueryOptions<
  ListActionsResponse,
  unknown,
  InfiniteData<TData, string | undefined>,
  QueryKey,
  string | undefined
>;

export const useQueryActions = <TData = ListActionsResponse>(
  request: Omit<ListActionsRequest, `pageToken`> = {},
  options?: Omit<UseQueryActionsOptions<TData>, `getNextPageParam` | `queryKey` | `queryFn` | `initialPageParam`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.actionsAll(request),
    queryFn: ({ pageParam, signal }) => listActions(authzUrl, { ...request, pageToken: pageParam }, { signal }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

export const useMutationCreateRole = (
  options?: Omit<UseMutationOptions<CreateRoleResponse, unknown, CreateRoleRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (request: CreateRoleRequest) => createRole(authzUrl, request),
    onSuccess: (...args) => {
      invalidateRolesCache(queryClient);
      options?.onSuccess?.(...args);
    },
  });
};

export const useMutationUpdateRole = (
  options?: Omit<UseMutationOptions<UpdateRoleResponse, unknown, UpdateRoleRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (request: UpdateRoleRequest) => updateRole(authzUrl, request),
    onSuccess: (...args) => {
      invalidateRolesCache(queryClient);
      options?.onSuccess?.(...args);
    },
  });
};

export const useMutationDeleteRole = (
  options?: Omit<UseMutationOptions<DeleteRoleResponse, unknown, DeleteRoleRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (request: DeleteRoleRequest) => deleteRole(authzUrl, request),
    onSuccess: (...args) => {
      invalidateRolesCache(queryClient);
      options?.onSuccess?.(...args);
    },
  });
};

export const useMutationAddRoleActionStatements = (
  options?: Omit<UseMutationOptions<AddActionStatementsResponse, unknown, AddActionStatementsRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (request: AddActionStatementsRequest) => addRoleActionStatements(authzUrl, request),
    onSuccess: (...args) => {
      invalidateRolesCache(queryClient);
      options?.onSuccess?.(...args);
    },
  });
};

export const useMutationRemoveRoleActionStatements = (
  options?: Omit<
    UseMutationOptions<RemoveActionStatementsResponse, unknown, RemoveActionStatementsRequest>,
    `mutationFn`
  >
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (request: RemoveActionStatementsRequest) => removeRoleActionStatements(authzUrl, request),
    onSuccess: (...args) => {
      invalidateRolesCache(queryClient);
      options?.onSuccess?.(...args);
    },
  });
};

// Users

export const getQueryOptionsUser = <TData = User>(
  authzUrl: string,
  userId: string,
  queryClient?: QueryClient // if provided, will search query cache for the initial data, which might speed up the loading significantly
): UseQueryOptions<User, unknown, TData> => {
  const name = userName.compile({ user: userId });
  return {
    initialData:
      queryClient &&
      (() => {
        const cachedListResponses = queryClient.getQueriesData<{ pages: ListUsersResponse[] }>({
          queryKey: cacheKeys.userAll(),
          exact: false,
        });
        for (const [, queryData] of cachedListResponses) {
          if (queryData?.pages) {
            for (const cachedResponse of queryData.pages) {
              const cachedUser = cachedResponse?.users?.find((u) => u.name === name);
              if (cachedResponse) return cachedUser;
            }
          }
        }
        return undefined;
      }),
    queryKey: cacheKeys.userOne(userId),
    queryFn: async ({ signal }) => {
      const response = await getUser(authzUrl, { name }, { signal });
      return response.user!;
    },
  };
};

export const useQueryUser = <TData = User>(userId?: string, options?: UseQueryOptions<User, unknown, TData>) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  const queries = !userId
    ? []
    : [
        {
          ...getQueryOptionsUser<TData>(authzUrl, userId, queryClient),
          ...options,
        },
      ];
  return useQueries({ queries }).at(0);
};

export type UseQueryUsersOptions<TData> = UseInfiniteQueryOptions<
  ListUsersResponse,
  unknown,
  InfiniteData<TData, string | undefined>,
  QueryKey,
  string | undefined
>;

export const useQueryUsers = <TData = ListUsersResponse>(
  request: Omit<ListUsersRequest, `pageToken`> = {},
  options?: Omit<UseQueryUsersOptions<TData>, `getNextPageParam` | `queryKey` | `queryFn` | `initialPageParam`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.userAll(request),
    queryFn: ({ pageParam, signal }) => listUsers(authzUrl, { ...request, pageToken: pageParam }, { signal }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

/**
 * Imperative, cached search for users and groups. Backed by TanStack Query's `fetchQuery`, so repeated
 * searches for the same filter are deduped/cached. Replaces ad-hoc `AuthzService` calls for typeahead search.
 */
export const useMembersSearch = () => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();

  const searchUsers = useCallback(
    (filter = '') =>
      queryClient.fetchQuery({
        queryKey: cacheKeys.usersSearch({ filter }),
        queryFn: ({ signal }) => listUsers(authzUrl, { filter }, { signal }),
        staleTime: 60 * 1_000,
      }),
    [authzUrl, queryClient]
  );

  const searchGroups = useCallback(
    (filter = '') =>
      queryClient.fetchQuery({
        queryKey: cacheKeys.groupsSearch({ filter }),
        queryFn: ({ signal }) => listGroups(authzUrl, { filter }, { signal }),
        staleTime: 60 * 1_000,
      }),
    [authzUrl, queryClient]
  );

  return { searchUsers, searchGroups };
};

// Workspaces

export const useMutationCreateWorkspace = (
  options?: Omit<UseMutationOptions<CreateWorkspaceResponse, unknown, CreateWorkspaceRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: CreateWorkspaceRequest) => {
      const response = await createWorkspace(authzUrl, request);
      invalidateWorkspacesCache(queryClient);
      return response;
    },
  });
};

export const useMutationDeleteWorkspace = (
  options?: Omit<UseMutationOptions<DeleteWorkspaceResponse, unknown, string>, `mutationFn`>
) => {
  return useWorkspaceStatusMutation((authzUrl, name) => deleteWorkspace(authzUrl, name), options);
};

export const useMutationUndeleteWorkspace = (
  options?: Omit<UseMutationOptions<UndeleteWorkspaceResponse, unknown, string>, `mutationFn`>
) => {
  return useWorkspaceStatusMutation((authzUrl, name) => undeleteWorkspace(authzUrl, name), options);
};

export const useMutationArchiveWorkspace = (
  options?: Omit<UseMutationOptions<ArchiveWorkspaceResponse, unknown, string>, `mutationFn`>
) => {
  return useWorkspaceStatusMutation((authzUrl, name) => archiveWorkspace(authzUrl, name), options);
};

export const useMutationUnarchiveWorkspace = (
  options?: Omit<UseMutationOptions<UndeleteWorkspaceResponse, unknown, string>, `mutationFn`>
) => {
  return useWorkspaceStatusMutation((authzUrl, name) => unarchiveWorkspace(authzUrl, name), options);
};

export const useMutationUpdateWorkspace = (
  options: Omit<UseMutationOptions<UpdateWorkspaceResponse, unknown, UpdateWorkspaceRequest>, `mutationFn`> = {}
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request) => {
      if (!request.workspace?.name) {
        throw new Error(`Workspace name is required for update`);
      }
      const response = await updateWorkspace(authzUrl, request);
      invalidateWorkspacesCache(queryClient);
      const { workspace: workspaceId } = workspaceName.parse(request.workspace.name);
      // Update the cache for the workspace detail query with returned data synchronously
      queryClient.setQueryData<Workspace>(cacheKeys.workspaceOne(workspaceId), response.workspace);
      // Invalidate the permission query for the workspace
      queryClient.invalidateQueries({
        queryKey: cacheKeys.permissionAllForResource(request.workspace.name),
        exact: false,
      });
      // Invalidate also the role-bindings which can change after workspace update
      queryClient.invalidateQueries({
        queryKey: cacheKeys.roleBindingsAll(),
        exact: false,
      });
      return response;
    },
  });
};

export const useMutationAddTagsToWorkspace = (
  options?: Omit<UseMutationOptions<AddTagsResponse, unknown, AddTagsRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: AddTagsRequest) => {
      const response = await addTagsToWorkspace(authzUrl, request);
      invalidateWorkspacesCache(queryClient);
      if (request.workspace) {
        const { workspace: workspaceId } = workspaceName.parse(request.workspace);
        queryClient.setQueryData<Workspace>(cacheKeys.workspaceOne(workspaceId), response.workspace);
      }
      return response;
    },
  });
};

export const useMutationRemoveTagsFromWorkspace = (
  options?: Omit<UseMutationOptions<RemoveTagsResponse, unknown, RemoveTagsRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: RemoveTagsRequest) => {
      const response = await removeTagsFromWorkspace(authzUrl, request);
      invalidateWorkspacesCache(queryClient);
      if (request.workspace) {
        const { workspace: workspaceId } = workspaceName.parse(request.workspace);
        queryClient.setQueryData<Workspace>(cacheKeys.workspaceOne(workspaceId), response.workspace);
      }
      return response;
    },
  });
};

export const useMutationAddLabelsToWorkspace = (
  options?: Omit<UseMutationOptions<AddLabelsResponse, unknown, AddLabelsRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: AddLabelsRequest) => {
      const response = await addLabelsToWorkspace(authzUrl, request);
      invalidateWorkspacesCache(queryClient);
      if (request.workspace) {
        const { workspace: workspaceId } = workspaceName.parse(request.workspace);
        queryClient.setQueryData<Workspace>(cacheKeys.workspaceOne(workspaceId), response.workspace);
      }
      return response;
    },
  });
};

export const useMutationRemoveLabelsFromWorkspace = (
  options?: Omit<UseMutationOptions<RemoveLabelsResponse, unknown, RemoveLabelsRequest>, `mutationFn`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (request: RemoveLabelsRequest) => {
      const response = await removeLabelsFromWorkspace(authzUrl, request);
      invalidateWorkspacesCache(queryClient);
      if (request.workspace) {
        const { workspace: workspaceId } = workspaceName.parse(request.workspace);
        queryClient.setQueryData<Workspace>(cacheKeys.workspaceOne(workspaceId), response.workspace);
      }
      return response;
    },
  });
};

export type UseQueryMyWorkspacesOptions<TData> = UseInfiniteQueryOptions<
  ListWorkspacesResponse,
  unknown,
  InfiniteData<TData, string | undefined>,
  QueryKey,
  string | undefined
>;

export const useQueryMyWorkspaces = <TData = ListWorkspacesResponse>(
  request: Omit<ListWorkspacesRequest, `pageToken`> = {},
  options?: Omit<UseQueryMyWorkspacesOptions<TData>, `getNextPageParam` | `queryKey` | `queryFn` | `initialPageParam`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.workspacesMy(request),
    queryFn: ({ pageParam, signal }) => listWorkspaces(authzUrl, { ...request, pageToken: pageParam }, { signal }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

export type UseQueryAllWorkspacesOptions<TData> = UseInfiniteQueryOptions<
  ListAllWorkspacesResponse,
  unknown,
  InfiniteData<TData, string | undefined>,
  QueryKey,
  string | undefined
>;

/**
 * An admin-only query for all workspaces in the system.
 */
export const useQueryAllWorkspaces = <TData = ListAllWorkspacesResponse>(
  request: Omit<ListAllWorkspacesRequest, `pageToken`> = {},
  options?: Omit<UseQueryAllWorkspacesOptions<TData>, `getNextPageParam` | `queryKey` | `queryFn` | `initialPageParam`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  return useInfiniteQuery({
    ...options,
    queryKey: cacheKeys.workspacesAll(request),
    queryFn: ({ pageParam, signal }) => listAllWorkspaces(authzUrl, { ...request, pageToken: pageParam }, { signal }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextPageToken || undefined,
  });
};

/**
 * Shared options for the query that validates the tab's active workspace. Used by
 * both `WorkspaceProvider` (to expose the validated active workspace to the whole
 * app shell) and the `RequireWorkspaces` guard's status hook, so the retry/staleTime
 * behavior of the deduped `useQueryWorkspace(activeWorkspaceId)` call stays in sync.
 */
export const activeWorkspaceValidationQueryOptions: Omit<
  UseQueryOptions<Workspace, unknown, Workspace>,
  `queryKey` | `queryFn` | `initialData`
> = {
  retry(failureCount, error) {
    const isNotFoundOrUnauthenticatedError = Boolean(
      isGrpcGatewayErrorResponse(error) &&
        (error.code === GrpcStatusCode.NOT_FOUND || error.code === GrpcStatusCode.PERMISSION_DENIED)
    );
    // skip retrying if the workspace is not found or user is not authenticated for the workspace, otherwise retry up to 3 times
    return !isNotFoundOrUnauthenticatedError && failureCount <= 3;
  },
  staleTime: 30 * 1000, // 30 seconds - the workspace is fetched in the `activateWorkspace` function, we can count on that result when validating after workspace switch
};

export const getQueryOptionsWorkspace = <TData = Workspace>(
  authzUrl: string,
  workspaceId: string,
  queryClient?: QueryClient // if provided, will search query cache for the initial data, which might speed up the loading significantly
): UseQueryOptions<Workspace, unknown, TData> => {
  const wsName = workspaceId && workspaceName.compile({ workspace: workspaceId });
  return {
    /**
     * This optimizes loading of workspace detail - it searches in cached ListWorkspaceResponses for the workspace name.
     * If found, the cached workspace is returned synchronously so the query has data from beginning, but it still runs
     * the query in the background to ensure the data is fresh.
     */
    initialData:
      queryClient &&
      (() => {
        const cachedResponseSources = [
          ...queryClient.getQueriesData<{ pages: ListWorkspacesResponse[] }>({
            queryKey: cacheKeys.workspacesAll(),
            exact: false,
          }),
          ...queryClient.getQueriesData<{ pages: ListWorkspacesResponse[] }>({
            queryKey: cacheKeys.workspacesMy(),
            exact: false,
          }),
        ];
        for (const [, queryData] of cachedResponseSources) {
          if (queryData?.pages) {
            for (const cachedResponse of queryData.pages) {
              const cachedWorkspace = cachedResponse?.workspaces?.find((ws) => ws.name === wsName);
              if (cachedResponse) return cachedWorkspace;
            }
          }
        }
        return undefined;
      }),
    queryKey: cacheKeys.workspaceOne(workspaceId),
    queryFn: async ({ signal }) => {
      const response = await getWorkspace(authzUrl, wsName, { signal });
      return response.workspace!;
    },
  };
};

export const useQueryWorkspace = <TData = Workspace>(
  workspaceId: string | undefined,
  options?: Omit<UseQueryOptions<Workspace, unknown, TData>, `queryKey` | `queryFn` | `initialData`>
) => {
  const cloudPlatformDiscovery = useCloudPlatformDiscovery();
  const authzUrl = cloudPlatformDiscovery?.authzUrl || '';
  const queryClient = useQueryClient();
  const queries = !workspaceId
    ? []
    : [
        {
          ...getQueryOptionsWorkspace<TData>(authzUrl, workspaceId, queryClient),
          ...options,
        },
      ];
  return useQueries({ queries }).at(0);
};
